<template>
  <div class="specs-container">
    <div v-for="(group, index) in specGroup" :key="index" class="spec-group">
      <h3 class="group-title">{{ group.title }}</h3>

      <!-- 型号标题行 -->
      <div class="model-header" v-if="hasUniqueValues(group)">
        <div class="title-placeholder"></div>
        <div v-for="(model, i) in models" :key="i" class="model-title">
          {{ model.name }}
        </div>
      </div>

      <!-- 参数项列表 -->
      <div class="spec-items">
        <div
          v-for="(item, itemIndex) in group.items"
          :key="itemIndex"
          class="spec-item"
        >
          <div class="spec-name">{{ item.name }}</div>

          <div class="spec-values" :class="{ 'common-value': item.isCommon }">
            <!-- 公共参数显示 -->
            <template v-if="item.isCommon">
              <div class="model-value common">
                {{ getCommonValue(item.key) }}
              </div>
            </template>

            <!-- 独立参数显示 -->
            <template v-else>
              <div
                v-for="(model, modelIndex) in models"
                :key="modelIndex"
                class="model-value"
                :data-model="model.name"
              >
                {{ getSpecValue(item.key, modelIndex) }}
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    models: {
      type: Array,
      required: true,
    },
    specGroup: {
      type: Array,
      required: true,
    },
    commonSpecs: {
      // 新增prop
      type: Object,
      default: () => ({}),
    },
  },
  methods: {
    getSpecValue(key, modelIndex) {
      const value = this.models[modelIndex].specs[key];
      return Array.isArray(value) ? value.join(", ") : value || "—";
    },
    // 新增公共值获取方法
    getCommonValue(key) {
      return this.commonSpecs[key] || "—";
    },

    // 判断分组是否需要显示型号标题
    hasUniqueValues(group) {
      return group.items.some((item) => !item.isCommon);
    },
  },
};
</script>
<style lang="less" scoped>
@import "~@/assets/styles/variables.less";
@import "~@/assets/styles/mixins.less";

.specs-container {
  // max-width: 1200px;
  margin: 0 auto;
  background: @color-bg-main;
  padding: 2rem 0;
  .mobile-only({
    padding: 1rem 0.5rem;
  });
}

.spec-group {
  margin-bottom: 3rem;
  border-bottom: 1px solid fade(@color-primary, 20%);

  &:last-child {
    border-bottom: none;
  }
}

.group-title {
  color: @color-text-title;
  font: 500 20px/1.4 @font-en;
  margin-bottom: 1.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid @color-primary;
}

.model-header {
  display: none;

  .tablet-up({
    display: grid;
    grid-auto-flow: column;                                 // 横向排布
    grid-template-columns: 200px repeat(auto-fit, minmax(150px, 1fr)); // 自动列数
    padding: 0.5rem 0;
    border-bottom: 1px solid fade(@color-primary, 15%);
    overflow-x: auto;                                       // 允许横向滚动
    -webkit-overflow-scrolling: touch;                      // 移动端惯性滚动
  });

  .desktop-up({
    display: grid;
    grid-auto-flow: column;
    grid-template-columns: 200px repeat(auto-fit, minmax(150px, 1fr));
    padding: 0.5rem 0;
    border-bottom: 1px solid fade(@color-primary, 15%);
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  });

  .title-placeholder {
    .tablet-up({
      width: 200px;
    });
    .desktop-up({
      width: 200px;
    });
  }

  .model-title {
    color: @color-text-title;
    font: 500 16px/1.4 @font-en;
    .tablet-up({
      text-align: left;
      min-width: 120px;
    });
    .desktop-up({
      text-align: left;
      min-width: 150px;
    });
  }
}

.spec-items {
  .mobile-only({
    padding: 0 1rem;
  });
}

.spec-item {
  display: flex;
  padding: 1.2rem 0;
  border-bottom: 1px solid fade(@color-primary, 15%);

  .mobile-only({
    flex-direction: column;
    padding: 1rem 0;
  });
}

.spec-name {
  flex: 0 0 200px;
  color: @color-text-sub;
  font: 500 16px/1.4 @font-en;
  padding-right: 2rem;

  .mobile-only({
    flex: none;
    width: 100%;
    padding-right: 0;
    margin-bottom: 0.5rem;
  });
}

.spec-values {
  flex: 1;
  display: flex;

  /* 平板/桌面端网格布局 */
  .tablet-up({
    display: grid !important;
    grid-auto-flow: column;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  });

  .desktop-up({
    display: grid !important;
    grid-auto-flow: column;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 1rem;
    overflow-x: auto;          // 👉 超出时允许横向滚动
    -webkit-overflow-scrolling: touch;
  });

  /* 移动端弹性布局 */
  .mobile-only({
    flex-direction: column; // 强制横向排列
    gap: 0.5rem 1rem;
    padding: 0.5rem 0;
  });
}
/* 公共参数样式 */
.common-value {
  // 平板/桌面端跨列显示
  .tablet-up({
      display: grid !important;
      grid-template-columns: 1fr !important; // 强制单列
      
      .model-value.common {
        // grid-column: 1 / -1; // 跨全部列
        width: 100%;
      }
    });
  .desktop-up({
      display: grid !important;
      grid-template-columns: 1fr !important; // 强制单列
      
      .model-value.common {
        // grid-column: 1 / -1; // 跨全部列
        width: 100%;
      }
    });
  // 移动端
  .mobile-only({
      .model-value.common {
        width: 100%;
        background: fade(@color-bg-light, 30%);
      }
    });
}

/* 优化后的model-value样式 */
.model-value {
  /* 公共样式 */
  color: @color-text-content;
  font: 400 16px/1.4 @font-en;
  min-width: 0; // 防止内容溢出
  padding-right: 0.3rem;

  /* 平板/桌面端 */
  .tablet-up({
    flex: 1; // 确保等宽分配
    text-align: left;
    display: flex;
    align-items: center;
    justify-content: flex-start;
  });
  .desktop-up({
    flex: 1; // 确保等宽分配
    text-align: left;
    display: flex;
    align-items: center;
    justify-content: flex-start;
  });

  .mobile-only({
    flex: 1 1 45%; // 两列布局
    min-width: 45%;
    display: flex;
    align-items: center;
    padding: 0.5rem;
    background: fade(@color-bg-light, 30%);
    border-radius: @radius-base;
    border: none;

    &::before {
      content: attr(data-model);
      color: @color-tips;
      font-size: 14px;
      font-weight: 500;
      margin-right: 0.8rem;
      flex-shrink: 0;
    }
  });

  .model-value.common {
    text-align: center; /*  - 文本居中 */

    /* 添加额外间距提高视觉舒适度 */
    padding: 0.8rem 0.5rem;
    width: 100%; /* 确保占据整个单元格 */

    /* 在移动端也保持居中 */
    .mobile-only({
    justify-content: center;
  });
  }

  /* 移动端布局 */
  .mobile-only({
    flex: 1 1 45%; // 两列布局
    min-width: 45%;
    display: flex;
    align-items: center;
    padding: 0.5rem;
    background: fade(@color-bg-light, 30%);
    border-radius: @radius-base;
    border: none;

    &::before {
      content: attr(data-model);
      color: @color-tips;
      font-size: 14px;
      font-weight: 500;
      margin-right: 0.8rem;
      flex-shrink: 0;
    }
  });
}

.common-value {
  .model-value.common {
    justify-content: center;

    .mobile-only({
      justify-content: flex-start;
    });
  }
}

.desktop-up({
  .spec-values::-webkit-scrollbar {
    height: 6px;
  }
  .spec-values::-webkit-scrollbar-thumb {
    background: fade(@color-primary, 30%);
    border-radius: 3px;
  }
});
</style>
